import { createReducer } from '@reduxjs/toolkit'
import cloneDeep from 'lodash/cloneDeep'
import flow from 'lodash/fp/flow'
import set from 'lodash/fp/set'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import mapValues from 'lodash/mapValues'
import { normalize } from 'normalizr'

import {
    // fetch dashboard
    fetchDashboardSuccess,
    fetchOrganizationSuccess,
} from 'actions/ui/dashboardPrintPage'
import { DATES } from 'const/filters'
import { serializeDatesFilter } from 'helpers/filters'
import {
    defaultWidgetState,
    defaultPaginationAndSorter,
    defaultWidgetLayout,
    defaultPage,
} from 'reducers/ui/defaults'
import { tabListSchema } from 'schemas'
import { Dashboard, DefaultPageState, Organization } from 'types'

interface DashboardPrintPageState extends DefaultPageState {
    dashboard: Dashboard | null
    organization: Organization | null
    organizationId?: string
    tabs: Record<string, unknown>
    widgets: Record<string, unknown>
}

const defaultState: DashboardPrintPageState = {
    dashboard: null,
    organization: null,
    tabs: {},
    widgets: {},
    ...defaultPage,
}

export default createReducer(cloneDeep(defaultState), (builder) => {
    builder
        // fetch dashboard
        .addCase(fetchDashboardSuccess, (state, action) => {
            const dashboard = action.payload
            const {
                entities: { tabs, widgets = {} },
            } = normalize(dashboard.config.tabs ?? [], tabListSchema)

            const legacyFilters = get(dashboard, ['config', 'filters'])

            const getTabFilters = (tab: any): any => {
                const filters =
                    cloneDeep(tab.localFilters || legacyFilters) ?? {}
                if (!isEmpty(filters[DATES])) {
                    filters[DATES] = serializeDatesFilter(filters[DATES])
                }
                // A saved lag is ignored: the widgets work it out from their
                // sources (features/Dashboards/rangeLag).
                delete filters.rangeLag
                return filters
            }

            return flow(
                set('dashboard', dashboard),
                set('organizationId', dashboard.organization_id),
                set(
                    'tabs',
                    mapValues(tabs, (tab) => ({
                        ...tab,
                        localFilters: { ...getTabFilters(tab) },
                    }))
                ),
                set(
                    'widgets',
                    mapValues(widgets, (widget) => ({
                        ...widget,
                        filters: {
                            ...widget.filters,
                            ...(!isEmpty(widget.filters[DATES]) && {
                                [DATES]: serializeDatesFilter(
                                    widget.filters[DATES]
                                ),
                            }),
                            rangeLag: undefined,
                        },
                        ...defaultWidgetState,
                        ...defaultPaginationAndSorter(widget),
                        ...defaultWidgetLayout(widget),
                    }))
                )
            )(state)
        })

        // fetch organization
        .addCase(fetchOrganizationSuccess, (state, action) => {
            const organization = action.payload
            return set(['organization'], organization, state)
        })
})
